import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, IconButton, Button, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const PURPLE = "#6F42C1";
/** Same left/right inset for title, message, and buttons */
const PAD_X = 24;

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(173, 181, 189, 0.55)",
    },
    "& .MuiDialog-paper": {
      margin: theme.spacing(2),
      borderRadius: "8px",
      padding: 0,
      maxWidth: "420px",
      width: "100%",
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.08)",
      overflow: "hidden",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: PAD_X,
    paddingRight: PAD_X,
    paddingTop: 20,
    paddingBottom: 16,
    boxSizing: "border-box",
  },
  title: {
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontWeight: 700,
    color: "#212529",
    lineHeight: "24px",
    margin: 0,
    padding: 0,
    letterSpacing: "-0.01em",
    flex: "1 1 auto",
    minWidth: 0,
  },
  closeButton: {
    flex: "0 0 auto",
    padding: 8,
    margin: 0,
    color: "#ADB5BD",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      color: "#868E96",
    },
  },
  body: {
    paddingLeft: PAD_X,
    paddingRight: PAD_X,
    paddingTop: 0,
    paddingBottom: 20,
    boxSizing: "border-box",
  },
  message: {
    fontFamily: "Inter, sans-serif",
    fontSize: "15px",
    fontWeight: 400,
    color: "#6C757D",
    lineHeight: "22px",
    margin: 0,
  },
  divider: {
    height: "1px",
    backgroundColor: "#DEE2E6",
    width: "100%",
    margin: 0,
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    paddingLeft: PAD_X,
    paddingRight: PAD_X,
    paddingTop: 16,
    paddingBottom: 20,
    boxSizing: "border-box",
  },
  cancelButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    textTransform: "none",
    borderRadius: "6px",
    padding: "8px 20px",
    minWidth: "88px",
    color: PURPLE,
    border: `1px solid ${PURPLE}`,
    backgroundColor: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      border: `1px solid ${PURPLE}`,
      color: PURPLE,
      boxShadow: "none",
    },
  },
  yesButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    textTransform: "none",
    borderRadius: "6px",
    padding: "8px 28px",
    minWidth: "96px",
    color: "#FFFFFF",
    backgroundColor: PURPLE,
    border: `1px solid ${PURPLE}`,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#5A32A3",
      borderColor: "#5A32A3",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      borderColor: "#E1E1E1",
      color: "#8F8F8F",
    },
  },
}));

const ReceiveBoxConfirmModal = ({
  open,
  onClose,
  onConfirm,
  loading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      className={classes.dialogRoot}
      disableEscapeKeyDown={loading}
    >
      <div className={classes.header}>
        <Typography component="h2" className={classes.title}>
          {t("Receive Box")}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
          size="small"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </div>
      <div className={classes.body}>
        <Typography className={classes.message}>
          {t("Are you sure you want to receive this box?")}
        </Typography>
      </div>
      <div className={classes.divider} />
      <div className={classes.actions}>
        <Button
          className={classes.cancelButton}
          onClick={onClose}
          disabled={loading}
        >
          {t("Cancel")}
        </Button>
        <Button
          className={classes.yesButton}
          onClick={onConfirm}
          disabled={loading}
        >
          {t("Yes")}
        </Button>
      </div>
    </Dialog>
  );
};

ReceiveBoxConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

ReceiveBoxConfirmModal.defaultProps = {
  loading: false,
};

export default ReceiveBoxConfirmModal;
